import clsx from 'clsx'
import { useState } from 'react'
import { UsageBadge } from './subcomponents/UsageBadge'
import { Code } from '../Code'
import json from '@shikijs/langs/json'
import dracula from '@shikijs/themes/dracula'
import { useTranslation } from 'react-i18next'
import { CompleteTraceView } from './subcomponents/CompleteTraceView'
import { SummarizedTraceView } from './subcomponents/SummarizedTraceView'

export type ChatEngineTrace = {
  id: string
  duration: number
  steps: {
    name: string
    type?: string
    duration: number
    depth: number
    error?: any
    events?: {
      type: string
      data?: Record<string, any>
    }[]
    usage?: {
      provider: string
      model: string
      properties?: Record<string, string | number | boolean>
      amount: number
    }[]
  }[]
}

export type ChatEngineTraceViewerProps = {
  trace: ChatEngineTrace
}

export function ChatEngineTraceViewer (props: ChatEngineTraceViewerProps) {
  const { trace } = props
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null)
  const selectedStep = selectedStepIndex === null ? null : trace.steps[selectedStepIndex]
  const [wrapText, setWrapText] = useState(false)
  const [summary, setSummary] = useState(true)
  const { t } = useTranslation()

  return (
    <div className="flex gap-2 h-full">
      <div className="w-96 flex flex-col">
        <div className="text-sm opacity-40">Trace id: {trace.id}</div>
        <div>Duration: {trace?.duration?.toFixed(1)}ms</div>
        <div className="flex flex-col overflow-hidden">
          <div className='flex justify-between items-center mt-4'>
            <h3 className="text-xl font-bold">Steps</h3>
            <label className="fieldset-label flex text-sm gap-2 hover:cursor-pointer">
              <input
                type="checkbox"
                className="toggle toggle-sm"
                checked={summary}
                onChange={() => setSummary(!summary)}
              />
              {t('chatEngineTrace:Summary')}
            </label>
          </div>
          <div className="overflow-auto shadow-inner" style={{ scrollbarWidth: 'thin' }}>
            {!summary && (
              <CompleteTraceView
                trace={trace}
                selectedStepIndex={selectedStepIndex}
                setSelectedStepIndex={setSelectedStepIndex}
              />
            )}
            {summary && (
              <SummarizedTraceView
                trace={trace}
                setSelectedStepIndex={setSelectedStepIndex}
              />
            )}
          </div>
        </div>
      </div>
      <div className='overflow-y-auto w-full border-l px-4' style={{ scrollbarWidth: 'thin' }}>
        {!selectedStep && (
          <div className="text-sm opacity-70">Select a step to view details</div>
        )}
        {selectedStep && (
          <div>
            {selectedStep.name} <span className="text-xs opacity-40">{selectedStep.duration?.toFixed(1)}ms</span>
            <div>
              <div className="flex justify-between items-center mt-4">
                <h3 className="text-xl font-bold">Events</h3>
                <label className="fieldset-label flex text-sm gap-2 hover:cursor-pointer">
                  <input
                    type="checkbox"
                    className="toggle toggle-sm"
                    checked={wrapText}
                    onChange={() => setWrapText(!wrapText)}
                  />
                  Wrap text
                </label>
              </div>
              {selectedStep.usage && (
                <div className="flex flex-wrap gap-1 my-2">
                  {selectedStep.usage.map((usage, index) => (
                    <UsageBadge key={index} usage={usage} />
                  ))}
                </div>
              )}
              {!selectedStep.events?.length && !selectedStep.error && (
                <div className="text-sm opacity-70">
                  No events registered in this step
                </div>
              )}
              {selectedStep.error && (
                <div>
                  Error
                  <Code
                    code={JSON.stringify(selectedStep.error, null, 2)}
                    lang={json}
                    theme={dracula}
                    className={clsx('text-sm my-2 overflow-x-auto bg-base-200', wrapText && 'wrapped')}
                  />
                </div>
              )}
              {selectedStep.events?.map((event, index) => (
                <div key={index}>
                  {event.type}
                  {event.data && (
                    <Code
                      code={JSON.stringify(event.data, null, 2)}
                      lang={json}
                      theme={dracula}
                      className={clsx('text-sm my-2 overflow-x-auto bg-base-200', wrapText && 'wrapped')}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
